import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { FileDown, FileSpreadsheet, Loader2, X } from 'lucide-react';
import workspaceService from '@/services/workspace.service';
import type { ExportBundle } from './export/bundle';
import { commonFields } from './engine';
import type { ReportDoc, Visual } from './model';

const box = 'w-full px-2 py-1.5 !text-xs rounded border border-gn-input bg-gn-input text-gn-primary focus:outline-none focus:border-gn-blue';

export const Modal: React.FC<{ title: string; onClose: () => void; wide?: boolean; children: React.ReactNode; footer?: React.ReactNode }> = ({
  title, onClose, wide, children, footer,
}) => (
  <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 backdrop-blur-sm" onMouseDown={onClose}>
    <div className={`bg-gn-primary border border-gn rounded-xl shadow-2xl flex flex-col max-h-[92vh] ${wide ? 'w-[min(1200px,95vw)] h-[88vh]' : 'w-[min(480px,95vw)]'}`}
         onMouseDown={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-gn">
        <span className="text-sm font-semibold text-gn-primary">{title}</span>
        <button type="button" onClick={onClose} className="p-1 text-gn-secondary hover:text-gn-primary"><X size={16} /></button>
      </div>
      <div className="flex-1 min-h-0 overflow-auto p-4">{children}</div>
      {footer && <div className="flex justify-end gap-2 px-4 py-2.5 border-t border-gn">{footer}</div>}
    </div>
  </div>
);

const primary = 'flex items-center gap-1.5 px-3 py-1.5 text-xs rounded bg-gn-blue text-gn-inverse disabled:opacity-50';
const secondary = 'px-3 py-1.5 text-xs rounded border border-gn text-gn-primary hover:bg-gn-tertiary';

/* ───────────── save into a workspace ───────────── */

export const SaveDialog: React.FC<{
  name: string;
  current?: string;
  onCancel: () => void;
  onSave: (workspace: string, name: string) => Promise<void>;
}> = ({ name, current, onCancel, onSave }) => {
  const { t } = useTranslation();
  const { data: workspaces = [], isLoading } = useQuery({ queryKey: ['workspaces'], queryFn: () => workspaceService.list() });
  const usable = workspaces.filter((w) => !w.archived);
  const [ws, setWs] = useState(current ?? '');
  const [title, setTitle] = useState(name);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    if (!ws && usable.length) setWs(usable[0].id);
  }, [usable, ws]);
  return (
    <Modal title={t('studio.save_title')} onClose={onCancel}
           footer={(
             <>
               <button type="button" className={secondary} onClick={onCancel}>{t('studio.cancel')}</button>
               <button type="button" className={primary} disabled={!ws || !title.trim() || busy}
                       onClick={async () => {
                         setBusy(true);
                         setError('');
                         try {
                           await onSave(ws, title.trim());
                         } catch (e: any) {
                           setError(e?.message ?? String(e));
                         } finally {
                           setBusy(false);
                         }
                       }}>
                 {busy && <Loader2 size={12} className="animate-spin" />} {t('studio.save')}
               </button>
             </>
           )}>
      <div className="space-y-3">
        <label className="block space-y-1">
          <span className="text-[11px] text-gn-secondary">{t('studio.report_name')}</span>
          <input className={box} value={title} onChange={(e) => setTitle(e.target.value)} autoFocus />
        </label>
        <label className="block space-y-1">
          <span className="text-[11px] text-gn-secondary">{t('studio.workspace')}</span>
          <select className={box} value={ws} onChange={(e) => setWs(e.target.value)} disabled={isLoading}>
            {usable.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
          </select>
        </label>
        {!isLoading && usable.length === 0 && <p className="text-[11px] text-gn-warning">{t('studio.no_workspace')}</p>}
        {error && <p className="text-[11px] text-gn-danger">{error}</p>}
      </div>
    </Modal>
  );
};

/* ───────────── combine tables ───────────── */

export const CombineDialog: React.FC<{
  doc: ReportDoc;
  onCancel: () => void;
  onCombine: (mode: 'join' | 'append', sources: string[], key: string, title: string) => void;
}> = ({ doc, onCancel, onCombine }) => {
  const { t } = useTranslation();
  const [mode, setMode] = useState<'join' | 'append'>('join');
  const [picked, setPicked] = useState<string[]>([]);
  const [key, setKey] = useState('');
  const [title, setTitle] = useState('');
  const common = useMemo(() => commonFields(doc, picked), [doc, picked]);
  useEffect(() => {
    if (!common.some((f) => f.key === key)) setKey(common.find((f) => f.format === 'text')?.key ?? common[0]?.key ?? '');
  }, [common, key]);
  const auto = picked.map((id) => doc.datasets.find((d) => d.id === id)?.title ?? '').filter(Boolean).join(' + ');
  const ok = picked.length >= 2 && (mode === 'append' || !!key);
  return (
    <Modal title={t('studio.combine_title')} onClose={onCancel}
           footer={(
             <>
               <button type="button" className={secondary} onClick={onCancel}>{t('studio.cancel')}</button>
               <button type="button" className={primary} disabled={!ok} onClick={() => onCombine(mode, picked, key, title.trim() || auto)}>
                 {t('studio.combine')}
               </button>
             </>
           )}>
      <div className="space-y-3 text-xs">
        <div className="grid grid-cols-2 gap-2">
          {(['join', 'append'] as const).map((m) => (
            <button key={m} type="button" onClick={() => setMode(m)}
                    className={`text-left p-2 rounded border ${mode === m ? 'border-gn-blue bg-gn-blue-light' : 'border-gn hover:bg-gn-tertiary'}`}>
              <div className="font-medium">{t(`studio.combine_${m}`)}</div>
              <div className="text-[10px] text-gn-secondary">{t(`studio.combine_${m}_hint`)}</div>
            </button>
          ))}
        </div>
        <div className="max-h-64 overflow-auto rounded border border-gn p-1.5 space-y-0.5">
          {doc.datasets.map((d) => (
            <label key={d.id} className="flex items-center gap-2 px-1 py-0.5 rounded hover:bg-gn-tertiary">
              <input type="checkbox" checked={picked.includes(d.id)}
                     onChange={(e) => setPicked(e.target.checked ? [...picked, d.id] : picked.filter((x) => x !== d.id))} />
              <span className="truncate">{d.title}</span>
            </label>
          ))}
        </div>
        {mode === 'join' && picked.length >= 2 && (
          <label className="block space-y-1">
            <span className="text-[11px] text-gn-secondary">{t('studio.join_on')}</span>
            {common.length ? (
              <select className={box} value={key} onChange={(e) => setKey(e.target.value)}>
                {common.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
              </select>
            ) : <p className="text-[11px] text-gn-warning">{t('studio.join_nothing_common')}</p>}
          </label>
        )}
        <label className="block space-y-1">
          <span className="text-[11px] text-gn-secondary">{t('studio.dataset_name')}</span>
          <input className={box} value={title} placeholder={auto} onChange={(e) => setTitle(e.target.value)} />
        </label>
      </div>
    </Modal>
  );
};

/* ───────────── export ───────────── */

const GROUPS: { kind: Visual['kind']; label: string }[] = [
  { kind: 'map', label: 'studio.kind_map' },
  { kind: 'table', label: 'studio.kind_tables' },
  { kind: 'pivot', label: 'studio.kind_pivots' },
  { kind: 'chart', label: 'studio.kind_charts' },
  { kind: 'text', label: 'studio.kind_texts' },
];

export const ExportDialog: React.FC<{
  doc: ReportDoc;
  format: 'pdf' | 'xlsx';
  onClose: () => void;
  bundle: (include: Set<string>, orientation: 'portrait' | 'landscape') => Promise<ExportBundle>;
}> = ({ doc, format, onClose, bundle }) => {
  const { t } = useTranslation();
  const [include, setInclude] = useState<Set<string>>(() => new Set(doc.visuals.map((v) => v.id)));
  const [orientation, setOrientation] = useState<'portrait' | 'landscape'>(doc.orientation);
  const [preview, setPreview] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const last = useRef<string | null>(null);
  const file = (doc.name || 'report').replace(/[\\/:*?"<>|]+/g, '_').trim() || 'report';

  const makePdf = async (): Promise<Blob> => {
    const [{ writeReportPdf, loadPdfFonts }, b] = await Promise.all([import('./export/pdfWriter'), bundle(include, orientation)]);
    const bytes = await writeReportPdf(b, await loadPdfFonts());
    return new Blob([bytes as BlobPart], { type: 'application/pdf' });
  };
  const makeXlsx = async (): Promise<Blob> => {
    const [{ writeReportXlsx }, b] = await Promise.all([import('./export/xlsxWriter'), bundle(include, orientation)]);
    const bytes = await writeReportXlsx(b);
    return new Blob([bytes as BlobPart], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  };

  // the PDF as it will come out, made again whenever the choice changes
  useEffect(() => {
    if (format !== 'pdf') return;
    let gone = false;
    const timer = window.setTimeout(async () => {
      if (include.size === 0) {
        setPreview(null);
        return;
      }
      setBusy(true);
      setError('');
      try {
        const blob = await makePdf();
        if (gone) return;
        const url = URL.createObjectURL(blob);
        if (last.current) URL.revokeObjectURL(last.current);
        last.current = url;
        setPreview(url);
      } catch (e: any) {
        if (!gone) setError(e?.message ?? String(e));
      } finally {
        if (!gone) setBusy(false);
      }
    }, 350);
    return () => {
      gone = true;
      window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [include, orientation, format]);

  useEffect(() => () => { if (last.current) URL.revokeObjectURL(last.current); }, []);

  const download = async () => {
    setBusy(true);
    setError('');
    try {
      const blob = format === 'pdf' ? await makePdf() : await makeXlsx();
      const a = document.createElement('a');
      const url = URL.createObjectURL(blob);
      a.href = url;
      a.download = `${file}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e: any) {
      setError(e?.message ?? String(e));
    } finally {
      setBusy(false);
    }
  };

  const toggle = (id: string, on: boolean) => {
    const next = new Set(include);
    if (on) next.add(id);
    else next.delete(id);
    setInclude(next);
  };

  const chooser = (
    <div className="space-y-3 text-xs">
      <p className="text-[11px] text-gn-secondary">{t(format === 'pdf' ? 'studio.export_pdf_hint' : 'studio.export_xlsx_hint')}</p>
      {GROUPS.map((g) => {
        const list = doc.visuals.filter((v) => v.kind === g.kind && (v.kind !== 'map' || doc.map));
        if (list.length === 0) return null;
        const all = list.every((v) => include.has(v.id));
        return (
          <div key={g.kind} className="space-y-0.5">
            <label className="flex items-center gap-2 font-semibold text-gn-secondary text-[11px] uppercase tracking-wide">
              <input type="checkbox" checked={all} onChange={(e) => {
                const next = new Set(include);
                list.forEach((v) => (e.target.checked ? next.add(v.id) : next.delete(v.id)));
                setInclude(next);
              }} />
              {t(g.label)} ({list.length})
            </label>
            {list.map((v) => (
              <label key={v.id} className="flex items-center gap-2 pl-5 py-0.5">
                <input type="checkbox" checked={include.has(v.id)} onChange={(e) => toggle(v.id, e.target.checked)} />
                <span className="truncate">{v.title || t(`studio.kind_${v.kind}`)}</span>
              </label>
            ))}
          </div>
        );
      })}
      {format === 'pdf' && (
        <div className="space-y-1 pt-2 border-t border-gn">
          <div className="text-[11px] font-semibold text-gn-secondary uppercase tracking-wide">{t('studio.orientation')}</div>
          <div className="flex rounded border border-gn overflow-hidden">
            {(['landscape', 'portrait'] as const).map((o) => (
              <button key={o} type="button" onClick={() => setOrientation(o)}
                      className={`flex-1 px-2 py-1 !text-xs !rounded-none ${orientation === o ? 'bg-gn-blue text-gn-inverse' : 'hover:bg-gn-tertiary'}`}>
                {t(`studio.${o}`)}
              </button>
            ))}
          </div>
        </div>
      )}
      {error && <p className="text-[11px] text-gn-danger break-words">{error}</p>}
    </div>
  );

  const footer = (
    <>
      <button type="button" className={secondary} onClick={onClose}>{t('studio.cancel')}</button>
      <button type="button" className={primary} disabled={busy || include.size === 0} onClick={download}>
        {busy ? <Loader2 size={12} className="animate-spin" /> : format === 'pdf' ? <FileDown size={12} /> : <FileSpreadsheet size={12} />}
        {t(format === 'pdf' ? 'studio.download_pdf' : 'studio.download_xlsx')}
      </button>
    </>
  );

  if (format === 'xlsx') {
    return <Modal title={t('studio.export_xlsx')} onClose={onClose} footer={footer}>{chooser}</Modal>;
  }
  return (
    <Modal title={t('studio.export_pdf')} onClose={onClose} wide footer={footer}>
      <div className="flex gap-4 h-full">
        <div className="w-72 shrink-0 overflow-auto">{chooser}</div>
        <div className="flex-1 min-w-0 rounded border border-gn bg-gn-tertiary relative">
          {preview ? <iframe title="pdf" src={preview} className="w-full h-full rounded" /> : (
            <div className="h-full flex items-center justify-center text-[11px] text-gn-muted">{include.size === 0 ? t('studio.nothing_chosen') : ''}</div>
          )}
          {busy && (
            <div className="absolute top-2 right-2 flex items-center gap-1.5 px-2 py-1 rounded bg-gn-primary border border-gn text-[11px]">
              <Loader2 size={12} className="animate-spin" /> {t('studio.preparing')}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};
